/**
 * `user_feedback` — обращения от пользователей мобильного приложения
 * (баги и предложения).
 *
 * Источник правды: миграция `supabase/migrations/20260518120000_user_feedback.sql`
 * в репо `stryzh`. INSERT делают пользователи мобилы, SELECT/UPDATE здесь —
 * только админ (RLS закрыт через `is_admin()`).
 *
 * Для воспроизведения багов админ смотрит timeline отладочных событий
 * (`client_debug_events`) по `session_id` обращения в окне ±30 минут вокруг
 * времени отправки — это даёт реальный поток действий пользователя без
 * необходимости дублировать breadcrumbs в саму строку обращения.
 */
import { createClient } from '@/api/supabase'

export type FeedbackType = 'bug' | 'idea'
export type FeedbackStatus = 'open' | 'resolved'

export interface UserFeedback {
  id: string
  created_at: string
  user_id: string | null
  session_id: string
  type: FeedbackType
  description: string
  screenshot_paths: string[]
  app_version: string | null
  build_number: number | null
  platform: string | null
  os_version: string | null
  device_model: string | null
  locale: string | null
  network_type: string | null
  route: string | null
  context: Record<string, unknown> | null
  status: FeedbackStatus
  resolved_at: string | null
  resolved_by: string | null
  admin_comment: string | null
}

/** Строка списка: к feedback пристёгнуто имя/телефон автора. */
export interface UserFeedbackRow extends UserFeedback {
  user_full_name: string | null
  user_phone: string | null
}

export interface ClientDebugEvent {
  id: string
  user_id: string | null
  created_at: string
  session_id: string
  platform: string
  app_version: string
  kind: string
  name: string
  outcome: string | null
  message: string
  payload: unknown
}

export interface ListUserFeedbackParams {
  status: FeedbackStatus
  /** 'all' — без фильтра по типу. */
  type?: FeedbackType | 'all'
  platform?: string
  version?: string
  page?: number
  pageSize?: number
}

export interface ListUserFeedbackResult {
  items: UserFeedbackRow[]
  total: number
}

/** Список обращений с фильтрами и пагинацией. */
export async function listUserFeedback(
  params: ListUserFeedbackParams,
): Promise<ListUserFeedbackResult> {
  const {
    status,
    type = 'all',
    platform,
    version,
    page = 1,
    pageSize = 50,
  } = params
  const supabase = createClient()

  let q = supabase
    .from('user_feedback')
    .select('*', { count: 'exact' })
    .eq('status', status)

  if (type !== 'all') q = q.eq('type', type)
  if (platform) q = q.eq('platform', platform)
  if (version) q = q.eq('app_version', version)

  q = q.order('created_at', { ascending: false })

  const from = (page - 1) * pageSize
  const to = from + pageSize - 1
  q = q.range(from, to)

  const { data, count, error } = await q
  if (error) throw new Error(error.message)

  const feedbacks = (data ?? []) as UserFeedback[]

  // Подтягиваем имена/телефоны авторов отдельным запросом — FK от user_id
  // указывает на auth.users, embedded JOIN с public.profiles напрямую
  // PostgREST не сделает. Дешевле один INNER batch, чем embed.
  const userIds = Array.from(
    new Set(feedbacks.map((f) => f.user_id).filter((v): v is string => !!v)),
  )

  const namesById = new Map<string, { full_name: string; phone: string }>()
  if (userIds.length > 0) {
    const { data: profiles, error: pErr } = await supabase
      .from('profiles')
      .select('id, full_name, phone')
      .in('id', userIds)
    if (pErr) throw new Error(pErr.message)
    for (const p of (profiles ?? []) as Array<{
      id: string
      full_name: string | null
      phone: string | null
    }>) {
      namesById.set(p.id, {
        full_name: p.full_name ?? '',
        phone: p.phone ?? '',
      })
    }
  }

  const items: UserFeedbackRow[] = feedbacks.map((f) => {
    const p = f.user_id ? namesById.get(f.user_id) : undefined
    return {
      ...f,
      user_full_name: p?.full_name ?? null,
      user_phone: p?.phone ?? null,
    }
  })

  return { items, total: count ?? 0 }
}

/** Одна карточка обращения по id. */
export async function getUserFeedback(id: string): Promise<UserFeedbackRow> {
  const supabase = createClient()

  const { data, error } = await supabase
    .from('user_feedback')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw new Error(error.message)

  const f = data as UserFeedback
  let user_full_name: string | null = null
  let user_phone: string | null = null
  if (f.user_id) {
    const { data: profile } = await supabase
      .from('profiles')
      .select('full_name, phone')
      .eq('id', f.user_id)
      .maybeSingle()
    const p = profile as { full_name: string | null; phone: string | null } | null
    user_full_name = p?.full_name ?? null
    user_phone = p?.phone ?? null
  }
  return { ...f, user_full_name, user_phone }
}

/**
 * Переключение статуса. Для status='resolved' выставляем resolved_at=now()
 * и resolved_by=auth.uid() серверно — клиент шлёт минимум, БД проверяет
 * консистентность через `user_feedback_resolved_consistency_chk`.
 *
 * `admin_comment` опционален — передаём только если меняется.
 */
export async function updateFeedbackStatus(args: {
  id: string
  status: FeedbackStatus
  adminComment?: string | null
}): Promise<void> {
  const supabase = createClient()

  const userId = (await supabase.auth.getUser()).data.user?.id ?? null

  // Готовим payload в соответствии с CHECK constraint:
  // open: resolved_at/by IS NULL
  // resolved: resolved_at NOT NULL
  const patch: Record<string, unknown> = {
    status: args.status,
  }
  if (args.status === 'resolved') {
    patch.resolved_at = new Date().toISOString()
    patch.resolved_by = userId
  } else {
    patch.resolved_at = null
    patch.resolved_by = null
  }
  if (args.adminComment !== undefined) {
    patch.admin_comment = args.adminComment
  }

  const { error } = await supabase
    .from('user_feedback')
    .update(patch)
    .eq('id', args.id)
  if (error) throw new Error(error.message)
}

/** Количество открытых обращений — для бабла в сайдбаре. */
export async function getOpenFeedbackCount(): Promise<number> {
  const supabase = createClient()
  const { count, error } = await supabase
    .from('user_feedback')
    .select('id', { count: 'exact', head: true })
    .eq('status', 'open')
  if (error) throw new Error(error.message)
  return count ?? 0
}

/**
 * Timeline отладочных событий вокруг времени обращения.
 * Окно по умолчанию — 30 минут до feedback.created_at (что юзер делал
 * перед отправкой). Дополнительно берём 1 минуту после — на случай если
 * на отправку ушло какое-то время.
 */
export async function getDebugEventsAround(args: {
  sessionId: string
  feedbackCreatedAt: string
  windowMinutesBefore?: number
}): Promise<ClientDebugEvent[]> {
  const { sessionId, feedbackCreatedAt, windowMinutesBefore = 30 } = args
  const supabase = createClient()

  const from = new Date(
    new Date(feedbackCreatedAt).getTime() - windowMinutesBefore * 60_000,
  ).toISOString()
  const to = new Date(
    new Date(feedbackCreatedAt).getTime() + 60_000,
  ).toISOString()

  const { data, error } = await supabase
    .from('client_debug_events')
    .select(
      'id, user_id, created_at, session_id, platform, app_version, kind, name, outcome, message, payload',
    )
    .eq('session_id', sessionId)
    .gte('created_at', from)
    .lte('created_at', to)
    .order('created_at', { ascending: true })
    .limit(500)
  if (error) {
    // RLS может закрывать таблицу для не-админов; страница для админов,
    // но если у юзера нет can_view_debug_logs — вернём пустой timeline,
    // карточка остаётся читаемой.
    return []
  }
  return (data ?? []) as ClientDebugEvent[]
}

/**
 * Signed URL на скриншот из bucket `user-feedback`. Bucket приватный, доступ
 * у админа есть через RLS (см. user_feedback_storage_select_owner_or_admin),
 * поэтому createSignedUrl сработает.
 */
export async function getFeedbackScreenshotUrl(
  path: string,
  expiresSec = 60 * 10,
): Promise<string | null> {
  const supabase = createClient()
  const { data, error } = await supabase.storage
    .from('user-feedback')
    .createSignedUrl(path, expiresSec)
  if (error) return null
  return data?.signedUrl ?? null
}
